import { Controller } from "@hotwired/stimulus"

// Fomantic UI Dropdown Controller - Design System v3
// Select 및 Combobox 컴포넌트용 드롭다운 기능
export default class extends Controller {
  static targets = ["input", "menu"]

  static values = {
    placeholder: { type: String, default: "선택하세요" },
    searchable: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    allowAdditions: { type: Boolean, default: false },
    maxSelections: Number,
    fullTextSearch: { type: Boolean, default: true },
    direction: { type: String, default: "auto" }  // 'upward' / 'downward' / 'auto'
  }

  connect() {
    // 즉시 초기화 시도
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      this.initializeDropdown()
    } else {
      // jQuery가 아직 로드되지 않은 경우 대기
      this.waitForJQuery().then(() => {
        this.initializeDropdown()
      })
    }
  }

  disconnect() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      try {
        $(this.element).find('.ui.dropdown').dropdown('destroy')
      } catch (e) {
        // ignore
      }
    }
  }

  waitForJQuery() {
    return new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
          clearInterval(checkInterval)
          resolve()
        }
      }, 50)

      // 최대 5초까지 대기
      setTimeout(() => {
        clearInterval(checkInterval)
        resolve()
      }, 5000)
    })
  }

  initializeDropdown() {
    if (typeof $ === 'undefined' || !$.fn || !$.fn.dropdown) {
      return
    }

    const $dropdown = $(this.element).find('.ui.dropdown')
    if ($dropdown.length === 0) return

    const self = this
    const options = {
      placeholder: this.placeholderValue,
      clearable: this.clearableValue,
      fullTextSearch: this.fullTextSearchValue,
      // 성능 최적화: 애니메이션 비활성화
      duration: 0,
      transition: 'none',
      message: {
        noResults: '검색 결과가 없습니다'
      },
      onAdd: function(addedValue, addedText, $addedChoice) {
        self.updatePlaceholder()
      },
      onRemove: function(removedValue, removedText, $removedChoice) {
        self.updatePlaceholder()
      },
      onChange: function(value, text, $selectedItem) {
        // Fomantic 이 hidden input value 를 자동 sync 하지만 일부 setup 에서 누락되는
        // 케이스가 있어 명시적으로 set + change 이벤트 dispatch (form 제출 시 신뢰성 확보).
        if (self.hasInputTarget) {
          self.inputTarget.value = value
          self.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    }

    // 검색 가능 드롭다운 설정
    if (this.searchableValue) {
      options.forceSelection = false
      options.selectOnKeydown = false
      options.showOnFocus = true
    }

    // 새 항목 추가 허용 (combobox)
    if (this.allowAdditionsValue) {
      options.allowAdditions = true
      options.hideAdditions = false
    }

    // 다중 선택 최대 개수
    if (this.hasMaxSelectionsValue) {
      options.maxSelections = this.maxSelectionsValue
    }

    // 펼침 방향 — auto 가 아니면 명시.
    if (this.directionValue && this.directionValue !== "auto") {
      options.direction = this.directionValue
    }

    $dropdown.dropdown(options)
  }

  // 프로그래밍 방식으로 값 설정
  setValue(value) {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.element).find('.ui.dropdown').dropdown('set selected', value)
    }
  }

  // 드롭다운 초기화
  clear() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.element).find('.ui.dropdown').dropdown('clear')
    }
  }

  // 드롭다운 리프레시
  refresh() {
    if (typeof $ !== 'undefined' && $.fn && $.fn.dropdown) {
      $(this.element).find('.ui.dropdown').dropdown('refresh')
    }
  }

  // 다중 선택 시 placeholder 업데이트
  updatePlaceholder() {
    if (!this.multipleValue) return

    const $dropdown = $(this.element).find('.ui.dropdown')
    const selectedCount = $dropdown.find('a.ui.label').length

    // MultiCombobox: input.search의 placeholder 업데이트
    const $searchInput = $dropdown.find('input.search')
    if ($searchInput.length > 0) {
      $searchInput.attr('placeholder', selectedCount > 0 ? '' : this.placeholderValue)
    }

    // MultiSelect: span.default.text 표시/숨김
    const $defaultText = $dropdown.find('span.default.text, span.text')
    if ($defaultText.length > 0) {
      if (selectedCount > 0) {
        $defaultText.text('').removeClass('default')
      } else {
        $defaultText.text(this.placeholderValue).addClass('default')
      }
    }
  }
}
